import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  CreateSupplierMappingMutationRequest,
  CreateSupplierMappingMutationResponse,
} from "../types/CreateSupplierMapping"

type CreateSupplierMappingClient = typeof client<
  CreateSupplierMappingMutationResponse,
  Error,
  CreateSupplierMappingMutationRequest
>
type CreateSupplierMapping = {
  data: CreateSupplierMappingMutationResponse
  error: Error
  request: CreateSupplierMappingMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<CreateSupplierMappingClient>>
  client: {
    parameters: Partial<Parameters<CreateSupplierMappingClient>[0]>
    return: Awaited<ReturnType<CreateSupplierMappingClient>>
  }
}
/**
 * @summary Create a supplier mapping
 * @link /v1/client-settings/create-supplier-mapping
 */
export function useCreateSupplierMapping(
  options: {
    mutation?: UseMutationOptions<
      CreateSupplierMapping["response"],
      CreateSupplierMapping["error"],
      CreateSupplierMapping["request"]
    >
    client?: CreateSupplierMapping["client"]["parameters"]
  } = {}
): UseMutationResult<
  CreateSupplierMapping["response"],
  CreateSupplierMapping["error"],
  CreateSupplierMapping["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    CreateSupplierMapping["response"],
    CreateSupplierMapping["error"],
    CreateSupplierMapping["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        CreateSupplierMapping["data"],
        CreateSupplierMapping["error"],
        CreateSupplierMapping["request"]
      >({
        method: "post",
        url: `/v1/client-settings/create-supplier-mapping`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
